import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { Link, useLocation } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import GroupIcon from "@mui/icons-material/Group";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import GpsFixed from "@mui/icons-material/GpsFixed";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";

import axios from "../configApi/axiosConfig";
import { APIURL } from "../configApi/apiConfig";
import { useAuth } from "./AuthContext/AuthContext";

export function SwipeableTemporaryDrawer({ userDataToken }) {
  const SIDEBAR_DENSITY = "compact";
  const isBalanced = SIDEBAR_DENSITY === "balanced";

  const [open, setOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [userData, setUserData] = useState([]);

  const { setMenuId } = useAuth();
  const location = useLocation();

  const userDisplayName =
    userDataToken?.Nombre ||
    userDataToken?.nombre ||
    userDataToken?.Usuario ||
    userDataToken?.usuario ||
    "Usuario";

  const userInitials = userDisplayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No token found");
          return;
        }

        const url = APIURL.getMenu(userDataToken.idUsuario);
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserData(response.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (userDataToken?.idUsuario) {
      fetchData();
    }
  }, [userDataToken]);

  const rootMenuItems = useMemo(
    () => userData.filter((item) => item.i_parent_id === null || Number(item.i_parent_id) === 0),
    [userData]
  );

  const createSubMenu = (parentId) =>
    userData
      .filter((item) => Number(item.i_parent_id) === Number(parentId))
      .map((item) => ({
        ...item,
        children: createSubMenu(item.i_idmenu_items),
      }));

  const structuredMenuItems = useMemo(
    () =>
      rootMenuItems.map((menuItem) => ({
        ...menuItem,
        children: createSubMenu(menuItem.i_idmenu_items),
      })),
    [rootMenuItems, userData]
  );

  const handleSubMenuToggle = (id) => {
    setOpenSubMenus((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const handleMenuClick = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    if (hasChildren) {
      handleSubMenuToggle(item.i_idmenu_items);
      return;
    }

    setMenuId(item.i_idmenu_items);
    setOpen(false);
  };

  const isRouteActive = (route) => {
    if (!route || route === "#") return false;
    return location.pathname === route || location.pathname.startsWith(`${route}/`);
  };

  const hasActiveRouteInTree = (node) => {
    if (!node) return false;

    if (isRouteActive(node.i_route)) {
      return true;
    }

    if (!node.children || node.children.length === 0) {
      return false;
    }

    return node.children.some((child) => hasActiveRouteInTree(child));
  };

  useEffect(() => {
    if (!structuredMenuItems.length) return;

    const expandedByRoute = {};

    const markParentsIfChildActive = (node) => {
      if (!node.children || node.children.length === 0) {
        return false;
      }

      const childHasActive = node.children.some((child) => markParentsIfChildActive(child) || hasActiveRouteInTree(child));

      if (childHasActive) {
        expandedByRoute[node.i_idmenu_items] = true;
      }

      return childHasActive || isRouteActive(node.i_route);
    };

    structuredMenuItems.forEach(markParentsIfChildActive);

    if (Object.keys(expandedByRoute).length > 0) {
      setOpenSubMenus((prev) => ({ ...prev, ...expandedByRoute }));
    }
  }, [structuredMenuItems, location.pathname]);

  const getIcon = (iconName) => {
    switch (iconName) {
      case "GroupIcon":
        return <GroupIcon />;
      case "ContentPasteSearchIcon":
        return <ContentPasteSearchIcon />;
      case "SecurityIcon":
        return <SecurityIcon />;
      case "PersonIcon":
        return <PersonIcon />;
      case "GpsFixed":
        return <GpsFixed />;
      case "PersonSearchIcon":
        return <PersonSearchIcon />;
      case "ManageAccountsIcon":
        return <ManageAccountsIcon />;
      default:
        return <DashboardCustomizeIcon />;
    }
  };

  const MenuNode = ({ item, depth = 0 }) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpenSubMenu = Boolean(openSubMenus[item.i_idmenu_items]);
    const isCurrentRouteActive = isRouteActive(item.i_route);
    const isChildRouteActive = hasChildren ? item.children.some((child) => hasActiveRouteInTree(child)) : false;
    const isActive = isCurrentRouteActive || isChildRouteActive;
    const targetRoute = item.i_route && item.i_route !== "#" ? item.i_route : undefined;

    const itemClassName = [
      `group relative ${isBalanced ? "mx-2 my-0.5 min-h-[40px] rounded-xl" : "mx-1.5 my-[2px] min-h-[36px] rounded-lg"} transition-all duration-200`,
      isActive
        ? "bg-morado/12 ring-1 ring-morado/25 shadow-sm"
        : "hover:bg-slate-100/90",
    ].join(" ");

    const textClassName = [
      `truncate ${isBalanced ? "text-[12.5px] leading-5" : "text-[12px] leading-[1.1rem]"}`,
      isCurrentRouteActive ? "font-bold text-morado" : isChildRouteActive ? "font-semibold text-slate-700" : "font-medium text-slate-700",
    ].join(" ");

    const iconContainerClassName = [
      `flex ${isBalanced ? "h-6.5 w-6.5 rounded-lg" : "h-6 w-6 rounded-md"} items-center justify-center transition-colors duration-200`,
      isActive ? "bg-white text-morado shadow-[0_1px_2px_rgba(15,23,42,0.08)]" : "bg-slate-100 text-slate-500 group-hover:text-morado",
    ].join(" ");

    const depthPaddingClass = isBalanced
      ? ["pl-3", "pl-7", "pl-11", "pl-14", "pl-[4.2rem]", "pl-[5.2rem]"][Math.min(depth, 5)]
      : ["pl-2.5", "pl-6", "pl-9", "pl-12", "pl-[3.6rem]", "pl-[4.5rem]"][Math.min(depth, 5)];

    const depthLinePositionClass = isBalanced
      ? ["left-[0.55rem]", "left-[1.55rem]", "left-[2.5rem]", "left-[3.3rem]", "left-[4.1rem]", "left-[5rem]"][Math.min(depth, 5)]
      : ["left-[0.45rem]", "left-[1.35rem]", "left-[2.15rem]", "left-[2.95rem]", "left-[3.7rem]", "left-[4.5rem]"][Math.min(depth, 5)];

    return (
      <div key={item.i_idmenu_items}>
        <ListItem disablePadding>
          <ListItemButton
            component={!hasChildren && targetRoute ? Link : "button"}
            to={!hasChildren && targetRoute ? targetRoute : undefined}
            onClick={() => handleMenuClick(item)}
            className={`${itemClassName} ${depthPaddingClass} ${isBalanced ? "pr-2" : "pr-1.5"}`}
          >
            {depth > 0 && (
              <span
                className={`absolute top-1/2 ${isBalanced ? "h-5" : "h-4"} w-[2px] -translate-y-1/2 rounded-full bg-slate-200 ${depthLinePositionClass}`}
              />
            )}

            <ListItemIcon className={isBalanced ? "!min-w-8" : "!min-w-7"}>
              <span className={iconContainerClassName}>{getIcon(item.i_icon)}</span>
            </ListItemIcon>

            <ListItemText
              primary={<span className={textClassName}>{item.i_name}</span>}
            />

            {hasChildren && (
              <ExpandMoreRoundedIcon
                className={[
                  `rounded-full border border-morado/15 bg-morado/10 ${isBalanced ? "p-[1px] text-[18px]" : "p-0 text-[17px]"} text-morado transition-all duration-200`,
                  isOpenSubMenu ? "rotate-0" : "-rotate-90",
                ].join(" ")}
              />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isOpenSubMenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding className="pb-1">
              {item.children.map((subMenu) => (
                <MenuNode key={subMenu.i_idmenu_items} item={subMenu} depth={depth + 1} />
              ))}
            </List>
          </Collapse>
        )}
      </div>
    );
  };

  const MenuSection = ({ title, items }) => {
    if (!items || items.length === 0) return null;

    return (
      <>
        <p className={`${isBalanced ? "px-4 pt-1.5 pb-1 text-[10px] tracking-[0.09em]" : "px-3.5 pt-1 pb-0.5 text-[9.5px] tracking-[0.1em]"} font-bold uppercase text-slate-500`}>
          {title}
        </p>

        {items.map((item) => (
          <MenuNode key={`${title}-${item.i_idmenu_items}`} item={item} />
        ))}
      </>
    );
  };

  return (
    <div>
      <button
        className={`ml-2 ${isBalanced ? "mr-4 p-2" : "mr-3 p-1.5"} rounded-full border border-white/25 bg-white/15 text-white shadow-sm transition-all duration-200 hover:scale-[1.03] hover:bg-white/25 focus:outline-none`}
        onClick={() => setOpen(true)}
      >
        <MenuIcon />
      </button>

      <SwipeableDrawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        PaperProps={{
          className:
            isBalanced
              ? "w-[300px] sm:w-[330px] rounded-tr-[14px] rounded-br-[14px] overflow-hidden border-r border-slate-200 shadow-[0_20px_45px_rgba(15,23,42,0.22)]"
              : "w-[290px] sm:w-[315px] rounded-tr-[12px] rounded-br-[12px] overflow-hidden border-r border-slate-200 shadow-[0_16px_36px_rgba(15,23,42,0.2)]",
        }}
      >
        <Box className={`${isBalanced ? "w-[300px] sm:w-[330px]" : "w-[290px] sm:w-[315px]"} h-full flex flex-col bg-slate-50`} role="presentation">
          <div className={`bg-morado ${isBalanced ? "px-3.5 py-3" : "px-3 py-2.5"} flex items-center justify-between border-b border-white/15`}>
            <Link to="/dashboard" className="rounded-md" onClick={() => setOpen(false)}>
              <img className={`${isBalanced ? "w-24 lg:w-28" : "w-20 lg:w-24"} mx-auto cursor-pointer drop-shadow-sm`} src="/img/logo.webp" alt="Logo Point" />
            </Link>
            <span className={`rounded-full border border-white/20 bg-white/10 ${isBalanced ? "px-2 py-0.5 text-[10px]" : "px-1.5 py-0.5 text-[9px]"} font-semibold text-white/85`}>
              Menú
            </span>
          </div>

          <div className={`border-b border-slate-200 bg-white/90 ${isBalanced ? "px-3 py-2.5" : "px-2.5 py-2"}`}>
            <div className={`flex items-center ${isBalanced ? "gap-2.5 rounded-xl px-2.5 py-2" : "gap-2 rounded-lg px-2 py-1.5"} border border-slate-200 bg-white shadow-sm`}>
              <div className={`flex ${isBalanced ? "h-9 w-9 text-xs" : "h-8 w-8 text-[11px]"} items-center justify-center rounded-full bg-morado font-bold text-white shadow-sm`}>
                {userInitials || "US"}
              </div>
              <div className="min-w-0">
                <p className={`truncate ${isBalanced ? "text-[13px]" : "text-[12px]"} font-semibold text-slate-700`}>{userDisplayName}</p>
                <p className={`${isBalanced ? "text-[10px]" : "text-[9px]"} font-medium text-slate-500`}>Perfil activo</p>
              </div>
            </div>
          </div>

          <div className={`border-b border-slate-200 bg-white/80 ${isBalanced ? "px-3 py-2" : "px-2.5 py-1.5"}`}>
            <p className={`${isBalanced ? "text-[10px] tracking-[0.09em]" : "text-[9px] tracking-[0.1em]"} font-semibold uppercase text-slate-500`}>
              Navegación principal
            </p>
          </div>

          <List className={`flex-1 overflow-y-auto ${isBalanced ? "py-1.5" : "py-1"} scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent`}>
            {structuredMenuItems.length > 0 ? (
              <MenuSection title="Módulos" items={structuredMenuItems} />
            ) : (
              <div className="px-4 py-8 text-center text-slate-500 text-sm">
                No hay opciones de menú disponibles.
              </div>
            )}
          </List>
        </Box>
      </SwipeableDrawer>
    </div>
  );
}
